import { motion, AnimatePresence } from "framer-motion";
import { EASE, EASE_CUT } from "@/lib/motion";

const RATIO = {
  v916: "aspect-[9/16] sm:aspect-[4/5] lg:aspect-[9/16]",
  r45: "aspect-[4/5]",
  w169: "aspect-[16/9]",
};

export const ProjectModal = ({ project, onClose, onBrief }) => (
  <AnimatePresence>
    {project && (
      <motion.div
        data-testid="project-modal"
        data-lenis-prevent
        className="fixed inset-0 z-[64] overflow-y-auto overscroll-contain bg-[#0a0a0a]"
        initial={{ clipPath: "inset(100% 0 0 0)" }}
        animate={{ clipPath: "inset(0% 0 0 0)" }}
        exit={{ clipPath: "inset(100% 0 0 0)" }}
        transition={{ duration: 0.8, ease: EASE_CUT }}
      >
        <div className="sticky top-0 z-10 flex items-center justify-between border-b border-[#1f1f1f] bg-[#0a0a0a]/92 px-5 py-4 backdrop-blur md:px-10">
          <span className="label text-[#d4ff00]">
            {project.index} / {project.category.toUpperCase()}
          </span>
          <button
            type="button"
            onClick={onClose}
            data-testid="project-modal-close"
            className="label border border-[#262626] px-4 py-2 text-[#a3a3a3] transition-colors hover:border-[#d4ff00] hover:text-[#d4ff00]"
          >
            CLOSE ✕
          </button>
        </div>

        <div className="mx-auto max-w-[1700px] px-5 py-12 md:px-10 md:py-20">
          <motion.h2
            initial={{ y: 40, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.9, ease: EASE, delay: 0.25 }}
            className="font-display text-4xl font-black uppercase leading-[0.88] tracking-[-0.04em] text-[#e5e5e5] md:text-[5vw]"
          >
            {project.name}
          </motion.h2>

          <div className="mt-8 grid gap-10 lg:grid-cols-12 lg:gap-14">
            <motion.div
              initial={{ clipPath: "inset(0 0 100% 0)" }}
              animate={{ clipPath: "inset(0 0 0% 0)" }}
              transition={{ duration: 1.1, ease: EASE, delay: 0.4 }}
              className={`relative w-full overflow-hidden bg-[#111] ${
                RATIO[project.treatment]
              } ${project.treatment === "w169" ? "lg:col-span-8" : "lg:col-span-5"}`}
            >
              {project.image ? (
                <img
                  src={project.image}
                  alt={`${project.name} — project still`}
                  onError={(e) => {
                    if (!e.currentTarget.dataset.fallback) {
                      e.currentTarget.dataset.fallback = "1";
                      e.currentTarget.src = e.currentTarget.src.replace(
                        "maxresdefault",
                        "hqdefault"
                      );
                    }
                  }}
                  className="h-full w-full object-cover"
                  style={{ objectPosition: project.objectPosition || "center" }}
                />
              ) : (
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                  <div
                    className="absolute inset-0"
                    style={{
                      background:
                        "radial-gradient(120% 120% at 50% 0%, #1a1a1a 0%, #0a0a0a 65%)",
                    }}
                  />
                  <div className="relative flex flex-col items-center px-6 text-center">
                    <img
                      src="/brand/hsk-logo.png"
                      alt="HSK"
                      className="h-7 w-auto"
                    />
                    <span className="mt-5 font-display text-xl font-black uppercase leading-[0.95] tracking-[-0.03em] text-[#e5e5e5] md:text-2xl">
                      {project.name}
                    </span>
                    <span className="mt-4 label text-[#a3a3a3]">
                      {project.format}
                    </span>
                  </div>
                </div>
              )}
              <div className="scanlines absolute inset-0 opacity-30" />
              <span className="absolute left-4 top-4 label border border-[#e5e5e5]/25 bg-[#0a0a0a]/70 px-3 py-1.5 text-[#e5e5e5] backdrop-blur">
                {project.badge}
              </span>
            </motion.div>

            <div
              className={
                project.treatment === "w169" ? "lg:col-span-4" : "lg:col-span-7"
              }
            >
              <p className="max-w-2xl font-display text-lg font-bold leading-snug tracking-tight text-[#e5e5e5] md:text-xl">
                {project.summary}
              </p>

              {project.link && (
                <a
                  href={project.link}
                  target="_blank"
                  rel="noreferrer"
                  data-testid={`project-modal-watch-${project.id}`}
                  className="group relative mt-8 inline-flex overflow-hidden border border-[#d4ff00] px-7 py-4"
                >
                  <span className="absolute inset-0 translate-y-full bg-[#d4ff00] transition-transform duration-500 ease-swift group-hover:translate-y-0" />
                  <span className="relative label text-[#d4ff00] transition-colors group-hover:text-[#0a0a0a]">
                    WATCH PROJECT ↗
                  </span>
                </a>
              )}

              <ul className="mt-10 border-t border-[#1f1f1f]">
                {project.notes.map((n, i) => (
                  <li
                    key={n}
                    className="flex items-baseline gap-5 border-b border-[#1f1f1f] py-4"
                  >
                    <span className="label text-[#d4ff00]">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <span className="text-sm text-[#c4c4c4]">{n}</span>
                  </li>
                ))}
              </ul>

              <dl className="mt-10 border-t border-[#1f1f1f]">
                {[
                  ["PROJECT", project.name],
                  ["CATEGORY", project.category],
                  ["TYPE", project.type],
                  ["ROLE", project.role],
                  ["FORMAT", project.format],
                  ["TOOLS", project.tools.join(" / ")],
                  ["YEAR", project.year],
                ].map(([k, v]) => (
                  <div
                    key={k}
                    className="flex items-baseline justify-between gap-6 border-b border-[#1f1f1f] py-4"
                  >
                    <dt className="label text-[#7a7a7a]">{k}</dt>
                    <dd className="text-right text-sm text-[#e5e5e5]">{v}</dd>
                  </div>
                ))}
              </dl>

              <button
                type="button"
                onClick={() => {
                  onClose();
                  setTimeout(onBrief, 500);
                }}
                data-testid="project-modal-cta"
                className="group relative mt-10 w-full overflow-hidden border border-[#262626] px-6 py-5 transition-colors hover:border-[#d4ff00]"
              >
                <span className="relative label text-[#a3a3a3] transition-colors group-hover:text-[#d4ff00]">
                  WANT SOMETHING LIKE THIS? →
                </span>
              </button>
            </div>
          </div>
        </div>
      </motion.div>
    )}
  </AnimatePresence>
);
